// =====================================================
// READ BLOGS — language / category rules for the search box
//
// The client-side twin of Server/utils/contentQuery.js; the two must agree.
// The existing local text filter (title / content / author) still decides
// whether a blog matches the TOPIC — this only adds the language and category
// conditions on top of it.
//
//   "Food"                 saved languages apply (unlabelled legacy blogs kept)
//   "Kannada"              Kannada blogs only — overrides saved languages
//   "Kannada Food"         Kannada blogs that match Food
//   "recipes in Kannada"   Kannada blogs that match "recipes"
//   "English breakfast…"   a topic, not a language: plain text search and no
//                          language condition at all
//   language dropdown      overrides all of the above for this search
//
// Nothing here is saved: an explicit language lasts for the current search.
// =====================================================

// Extra spellings a reader may type (never stored). Keyed by the stored name.
const LANGUAGE_ALIASES = {
  Hindi: ["हिन्दी", "हिंदी"],
  Telugu: ["తెలుగు"],
  Kannada: ["ಕನ್ನಡ"],
  Tamil: ["தமிழ்"],
  Malayalam: ["മലയാളം"],
  Marathi: ["मराठी"],
  Bengali: ["বাংলা", "bangla"],
};

const FILLER = new Set([
  "blog", "blogs", "post", "posts", "article", "articles",
  "story", "stories", "language", "content",
]);

export const ALL_LANGUAGES = "all";

const lower = (s) => String(s == null ? "" : s).trim().toLowerCase();
const stripEdges = (w) => w.replace(/^[^\p{L}\p{N}\p{M}]+|[^\p{L}\p{N}\p{M}]+$/gu, "");

function lookup(list, aliases = {}) {
  const map = new Map();
  for (const name of list) {
    map.set(lower(name), name);
    for (const a of aliases[name] || []) map.set(lower(a), name);
  }
  return (value) => (typeof value === "string" ? map.get(lower(value)) || null : null);
}

// interpretQuery("Kannada Food", options) →
//   { text: "Food", languages: ["Kannada"], category: "Food",
//     languageMentioned: true, explicitLanguage: true }
export function interpretQuery(raw, options) {
  const canonicalLanguage = lookup(options.languages, LANGUAGE_ALIASES);
  const canonicalCategory = lookup(options.categories);

  const query = String(raw == null ? "" : raw).trim();
  const cleaned = query.split(/\s+/).filter(Boolean).map(stripEdges);
  const langAt = cleaned.map((w) => canonicalLanguage(w));
  const topicWords = (list) =>
    list.filter((w) => w && !FILLER.has(w.toLowerCase()) && w.toLowerCase() !== "in");

  if (!langAt.some(Boolean)) {
    return {
      text: query,
      languages: [],
      category: canonicalCategory(topicWords(cleaned).join(" ")),
      languageMentioned: false,
      explicitLanguage: false,
    };
  }

  const languages = [...new Set(langAt.filter(Boolean))];
  const rest = topicWords(cleaned.filter((_, i) => !langAt[i]));
  const restCategory = rest.length ? canonicalCategory(rest.join(" ")) : null;
  const last = cleaned.length - 1;
  const endsWithInLanguage =
    Boolean(langAt[last]) && last >= 1 && cleaned[last - 1].toLowerCase() === "in";

  if (!(rest.length === 0 || restCategory || endsWithInLanguage)) {
    return { text: query, languages: [], category: null, languageMentioned: true, explicitLanguage: false };
  }
  return { text: rest.join(" "), languages, category: restCategory, languageMentioned: true, explicitLanguage: true };
}

// null = no language condition; otherwise { languages, includeUnlabelled, source }.
export function languageScope({ interpretation, filterLanguage, preferredLanguages, searching, options }) {
  if (filterLanguage === ALL_LANGUAGES) return null;
  const canonicalLanguage = lookup(options.languages, LANGUAGE_ALIASES);

  const picked = canonicalLanguage(filterLanguage);
  if (picked) return { languages: [picked], includeUnlabelled: false, source: "filter" };
  if (interpretation?.languages?.length) {
    return { languages: interpretation.languages, includeUnlabelled: false, source: "query" };
  }
  if (interpretation?.languageMentioned) return null;

  const prefs = [...new Set((preferredLanguages || []).map(canonicalLanguage).filter(Boolean))];
  if (searching && prefs.length) return { languages: prefs, includeUnlabelled: true, source: "preferences" };
  return null;
}

export function inLanguageScope(blog, scope) {
  if (!scope) return true;
  if (!blog.language) return scope.includeUnlabelled;
  return scope.languages.includes(blog.language);
}

// The existing Read Blogs text match, unchanged: title, content or author
// contains the (lower-cased) text.
const textMatches = (blog, s) =>
  blog.title?.toLowerCase().includes(s) ||
  blog.content?.toLowerCase().includes(s) ||
  blog.author?.toLowerCase().includes(s);

// Builds the filter for one search. `null` means "no search active" (show
// the feed as it is).
//   query              what is typed in the search box
//   filterLanguage     "" (defaults) | "all" | a language name
//   filterCategory     "" | a category name
//   preferredLanguages the reader's saved languages
//   options            { languages, categories }
export function buildBlogFilter({ query, filterLanguage, filterCategory, preferredLanguages, options }) {
  const hasQuery = Boolean(String(query || "").trim());
  if (!hasQuery && !filterLanguage && !filterCategory) return null;

  const interpretation = interpretQuery(query, options);
  const text = interpretation.text.trim().toLowerCase();
  const scope = languageScope({
    interpretation,
    filterLanguage,
    preferredLanguages,
    searching: Boolean(text) || Boolean(filterCategory),
    options,
  });

  return {
    interpretation,
    scope,
    matches(blog) {
      if (!inLanguageScope(blog, scope)) return false;
      if (filterCategory && blog.category !== filterCategory) return false;
      if (!text) return true;
      // A query naming a category also matches blogs filed under it.
      if (interpretation.category && blog.category === interpretation.category) return true;
      return textMatches(blog, text);
    },
  };
}
